import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { THEME_STORAGE_KEY, applyTheme, otherTheme, readTheme } from '@/lib/theme';
import type { Theme } from '@/lib/theme';

type ThemeState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeState | null>(null);

/**
 * Tema claro e escuro — RNF-25.
 *
 * ================================================================
 * O PROVEDOR NÃO É QUEM APLICA O TEMA NA ABERTURA
 * ================================================================
 *
 * Quem aplica é o script inline do `index.html`, que roda antes do primeiro
 * pixel. Se a primeira aplicação acontecesse aqui, a página pintaria clara e
 * escureceria depois — o flash branco que a tarefa pede para evitar.
 *
 * Este provedor faz outra coisa: guarda o estado em memória para os componentes
 * lerem, e reaplica quando o usuário TROCA. Ele nasce lendo o mesmo
 * `localStorage` que o script leu, então os dois concordam desde o início.
 *
 * ================================================================
 * DUAS OPÇÕES, E NÃO TRÊS
 * ================================================================
 *
 * Não há "seguir o sistema". Foi decisão explícita: o padrão é claro, e quem
 * quiser escuro escolhe uma vez. Seguir o sistema operacional mudaria a
 * aparência sozinho para quem já usa o app há meses.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readTheme(window.localStorage));

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);

    applyTheme(next, document.documentElement);

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Sem persistência o tema vale só nesta aba — melhor que quebrar a troca.
    }
  }, []);

  const toggle = useCallback(() => setTheme(otherTheme(theme)), [setTheme, theme]);

  /**
   * A mesma escolha em todas as abas abertas.
   *
   * `storage` dispara nas OUTRAS abas quando uma delas grava. Sem isto, trocar
   * o tema numa aba deixaria a outra clara até um recarregamento — e o usuário
   * concluiria que a preferência não salvou.
   */
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== THEME_STORAGE_KEY) return;

      const next = readTheme(window.localStorage);

      setThemeState(next);
      applyTheme(next, document.documentElement);
    }

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);

  if (context === null) {
    throw new Error('useTheme precisa estar dentro de <ThemeProvider>.');
  }

  return context;
}
